// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, isValidElement } from '../../utils';
import { safeInvoke } from '../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Overlay from '../Overlay';
import Form from '../Form';
import Button from '../Button';
import Translate from '../Translate';

import '../../helpers/base.less';
import './Dialog.less';

/**
 * `Dialog` component.
 */
class Dialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            show: props.isOpen,
        };
    }

    componentWillReceiveProps(props) {
        const newState = {
            show: props.isOpen,
        };

        this.setState(newState);
    }

    handleClose = () => {
        this.setState({ show: false });
        safeInvoke(this.props.onClose);
    };

    renderImage(image, block) {
        const { baseClassName } = this.props;
        const classPrefix = `${baseClassName}__${block}`;

        if (isValidElement(image)) {
            return <div className={classPrefix}>{image}</div>;
        }

        if (typeof image === 'string') {
            return (
                <div className={classPrefix}>
                    <img className={`${classPrefix}-inner`} src={image} />
                </div>
            );
        }

        return (
            <div className={classPrefix}>
                <img className={`${classPrefix}-inner`} {...image} />
            </div>
        );
    }

    render() {
        const {
            baseClassName,
            className,
            children,
            isOpen,
            banner,
            image,
            title,
            cancelTitle,
            buttons,
            form,
            size,
            onClose,
            ...props
        } = this.props;

        const { show } = this.state;

        const cancelTitleElement = cancelTitle || <Translate content="Dialog.cancelButton" fallback="Cancel" />;

        let childrenElement;
        if (form) {
            form.cancelButton = {
                onClick: this.handleClose,
                ...form.cancelButton,
            };
            form.footerClassName = classNames(form.footerClassName, `${baseClassName}__form-footer`);
            form.applyButton = form.applyButton || false;

            if (!form.cancelButton.children && cancelTitleElement) {
                form.cancelButton.children = cancelTitleElement;
            }

            childrenElement = <Form {...form}>{children}</Form>;
        } else {
            childrenElement = children;
        }

        return (
            <Overlay
                className={classNames(baseClassName, className)}
                isOpen={show}
                onClose={this.handleClose}
                canCloseOnBackdropClick={false}
                size={size}
                {...props}
            >
                {title && (
                    <div className={`${baseClassName}__heading`}>
                        <h2 className={`${baseClassName}__heading-title`}>
                            {title}
                        </h2>
                    </div>
                )}

                {banner && this.renderImage(banner, 'banner')}

                <div className={`${baseClassName}__content`}>
                    {image && this.renderImage(image, 'image')}

                    {childrenElement}

                    {!form && (
                        <div className={`${baseClassName}__buttons`}>
                            {buttons}
                            <Button onClick={this.handleClose}>{cancelTitleElement}</Button>
                        </div>
                    )}
                </div>
            </Overlay>
        );
    }
}

Dialog.propTypes = {
    /**
     * Toggles visibility.
     */
    isOpen: PropTypes.bool,
    /**
     * Banner. Renders in front of content as wide as dialog.
     */
    banner: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.element,
    ]),
    /**
     * Image. Renders into content.
     */
    image: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.element,
    ]),
    /**
     * Title
     */
    title: PropTypes.node,
    /**
     * Title of "cancel" button
     */
    cancelTitle: PropTypes.node,
    /**
     * Buttons except "cancel" button
     */
    buttons: PropTypes.node,
    /**
     * Wraps children into [Form](#!/Form) if not empty.
     */
    form: PropTypes.object,
    /**
     * Dialog width
     */
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    /**
     * On close event handler. Called immediately after dialog closed by "cancel" button or "Esc" key.
     */
    onClose: PropTypes.func,
    /**
     * Content of the `DialogWindow`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Dialog.defaultProps = {
    isOpen: false,
    banner: null,
    image: null,
    title: null,
    cancelTitle: null,
    buttons: null,
    form: null,
    size: 'md',
    onClose: null,
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}dialog`,
};

export default Dialog;



// WEBPACK FOOTER //
// ./components/Dialog/Dialog.js